import type { MouseEvent } from "react";
import { FloatingTab, FloatingTabBar } from "@ferrivia/ui";
import { NavLink } from "react-router";
import type { ClientTabPath } from "@ferrivia/analytics";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { tabsFor } from "../../app/routes";
import { useTabNavigation } from "../../app/useTabNavigation";
import { useChatSummary } from "../../hooks/useChatSummary";
import s from "./TabBar.module.css";

export interface TabBarProps {
  /** Slides the bar off the bottom of the screen — the shell sets this while
      the on-screen keyboard is up. The bar stays mounted so its unread poll
      keeps its cadence across a keyboard open/close instead of restarting. */
  collapsed?: boolean;
}

export function TabBar({ collapsed = false }: TabBarProps) {
  const { t } = useTranslation("client");
  const market = useMarket();
  const { goToTab } = useTabNavigation();
  const chatSummary = useChatSummary();
  const unreadCount = chatSummary.status === "success" ? chatSummary.summary.unreadCount : 0;

  const labels: Record<string, string> = {
    "nav.home": t("nav.home", "Etusivu"),
    "nav.infusion": t("nav.infusion", "Rautainfuusio"),
    "nav.support": t("nav.support", "Kartoitus"),
    "nav.results": t("nav.results", "Tulokset"),
    "nav.chat": t("nav.chat", "Chat"),
  };

  /* The tab stays a real `NavLink`, so it keeps its `href`, its keyboard
     activation and the `aria-current` the pill's active styling is keyed off.
     Only the *navigation* is taken over, and only for the plain left click
     that means "go here in this app" — a modified or middle click still means
     "open this somewhere else", which is the browser's to handle. */
  function handleTabClick(event: MouseEvent<HTMLAnchorElement>, path: ClientTabPath) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    goToTab(path, "tab_bar");
  }

  return (
    <FloatingTabBar collapsed={collapsed} className={s.root} data-testid="tab-bar">
      {tabsFor(market).map(({ path, labelKey, icon: Icon }) => (
        <FloatingTab
          key={path}
          as={NavLink}
          to={path}
          end={path === "/"}
          onClick={(event: MouseEvent<HTMLAnchorElement>) => handleTabClick(event, path)}
          icon={
            <>
              <Icon aria-hidden size={22} strokeWidth={1.75} />
              {path === "/chat" && unreadCount > 0 ? (
                <span
                  className={s.badge}
                  aria-label={t("tabBar.unreadAriaLabel", { count: unreadCount, defaultValue: "{{count}} lukematonta viestiä" })}
                />
              ) : null}
            </>
          }
          label={labels[labelKey]}
        />
      ))}
    </FloatingTabBar>
  );
}
